import { create } from 'zustand';
import type { Reservation } from '@/lib/types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ReservationState {
  reservations: Reservation[];
  lastSync: number | null;

  setReservations: (reservations: Reservation[]) => void;
  setLastSync: (ts: number) => void;
  search: (query: string) => Reservation[];
  getByTable: (tableId: string) => Reservation[];
  getTodayReservations: () => Reservation[];
  getActiveReservations: () => Reservation[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Check whether a reservation matches a partial search query.
 * Matches against client_name, client_phone, qr_code, and table_id.
 */
function matchesQuery(reservation: Reservation, queryLower: string): boolean {
  const fields: (string | undefined)[] = [
    reservation.client_name,
    reservation.client_phone,
    reservation.qr_code,
    reservation.table_id,
    reservation.id,
  ];

  return fields.some((field) => {
    if (!field) return false;
    return field.toString().toLowerCase().includes(queryLower);
  });
}

// ---------------------------------------------------------------------------
// Store (runtime only — NOT persisted)
// ---------------------------------------------------------------------------

export const useReservationStore = create<ReservationState>()((set, get) => ({
  reservations: [],
  lastSync: null,

  setReservations: (reservations) => set({ reservations }),

  setLastSync: (ts) => set({ lastSync: ts }),

  search: (query) => {
    if (!query || query.trim() === '') return [];

    const queryLower = query.toLowerCase().trim();
    const { reservations } = get();

    // Use a Set to deduplicate results
    const seen = new Set<string>();
    const results: Reservation[] = [];

    for (const resa of reservations) {
      if (matchesQuery(resa, queryLower) && !seen.has(resa.id)) {
        seen.add(resa.id);
        results.push(resa);
      }
    }

    return results;
  },

  getByTable: (tableId) => {
    const { reservations } = get();
    return reservations.filter((r) => r.table_id === tableId);
  },

  getTodayReservations: () => {
    const { reservations } = get();
    // Today's date in ISO format (YYYY-MM-DD)
    const today = new Date().toISOString().split('T')[0];
    return reservations.filter((r) => r.date?.startsWith(today));
  },

  getActiveReservations: () => {
    const { reservations } = get();
    return reservations.filter(
      (r) => r.status === 'confirmed' || r.status === 'pending',
    );
  },
}));
